import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AssessmentCard from '@components/assessment/AssessmentCard';
import GradientBackground from '@components/ui/GradientBackground';
import { useTheme } from '@theme/useTheme';
import { trackGtmButtonClick } from '@/analytics/gtm';
import { FaHistory, FaRedo, FaHome, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaChartLine } from 'react-icons/fa';
import type { ReadOnlyFormProps } from '../../types/form.types';

const AuthenticatedResults: React.FC<ReadOnlyFormProps> = ({ formData }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [bgColor, setBgColor] = React.useState<string>(colors.primaryButton);
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({});

  const riskScore = formData.riskScore || 0;
  const riskLevel = formData.riskLevel || 'Low';
  const riskFactors = formData.riskFactors || [];
  const recommendations = formData.recommendations || [];

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleRetakeAssessment = () => {
    navigate('/assessment', { state: { resetAssessment: true } });
  };

  const handleViewHistory = () => {
    navigate('/assessment/history');
  };

  const handleViewResults = () => {
    // Use the actual values from the component state, not the potentially stale formData
    navigate('/results', {
      state: {
        formData: {
          riskScore: riskScore,
          riskLevel: riskLevel,
          riskFactors: riskFactors,
          recommendations: recommendations,
          bmi: formData.bmi,
          weight: formData.weight,
          heightFeet: formData.heightFeet,
          heightInch: formData.heightInch
        }
      }
    });
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high':
        return <FaExclamationTriangle className="w-5 h-5 text-red-500" />;
      case 'moderate':
        return <FaInfoCircle className="w-5 h-5 text-yellow-500" />;
      case 'low':
        return <FaCheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <FaCheckCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.background }}>
      <div className="flex-1">
        {/* Hero Section */}
        <div className="relative pt-24 pb-20 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-28 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
          <GradientBackground position="top" height="large" />

          <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
            <div className="max-w-7xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <motion.h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                  style={{ color: colors.heading }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  Your Sleep Apnea Assessment Results
                </motion.h1>
                <motion.p
                  className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                  style={{ color: colors.text }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                >
                  Your assessment has been saved to your account. Here's your personalized risk assessment and recommendations.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">

            {/* Assessment Results */}
            <section className="mt-4 mb-8 sm:mb-12">
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Assessment Card - Takes up 2 columns on large screens */}
                <motion.div
                  className="lg:col-span-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <AssessmentCard
                    data={{
                      risk_score: riskScore,
                      risk_level: riskLevel,
                      taken_at: new Date(),
                    }}
                    bgColor={bgColor}
                    setBgColor={setBgColor}
                  />
                </motion.div>

                {/* Action Buttons Sidebar */}
                <motion.div
                  className="lg:col-span-1 space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {/* Quick Actions */}
                  <div className="rounded-tr-3xl rounded-bl-3xl border border-black p-6" style={{ backgroundColor: colors.background }}>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: colors.heading }}>
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_auth_quick_history',
                            section: 'page_results_authenticated',
                            label: 'View Previous Results',
                            destination: '/assessment/history',
                          });
                          handleViewHistory();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.primaryButton, color: 'white' }}
                      >
                        <FaHistory className="w-5 h-5" />
                        View Previous Results
                      </button>
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_auth_quick_view_page',
                            section: 'page_results_authenticated',
                            label: 'View Results Page',
                            destination: '/results',
                          });
                          handleViewResults();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.primaryButton, color: 'white' }}
                      >
                        <FaChartLine className="w-5 h-5" />
                        View Results Page
                      </button>
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_auth_quick_retake',
                            section: 'page_results_authenticated',
                            label: 'Retake Assessment',
                            destination: '/assessment',
                          });
                          handleRetakeAssessment();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.secondaryButton, color: colors.text }}
                      >
                        <FaRedo className="w-5 h-5" />
                        Retake Assessment
                      </button>
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_auth_quick_home',
                            section: 'page_results_authenticated',
                            label: 'Back to Home',
                            destination: '/',
                          });
                          handleGoHome();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.input, color: colors.text }}
                      >
                        <FaHome className="w-5 h-5" />
                        Back to Home
                      </button>
                    </div>
                  </div>

                  {/* Risk Level Summary */}
                  <div className="rounded-tr-3xl rounded-bl-3xl border border-black p-6" style={{ backgroundColor: colors.background }}>
                    <div className="flex items-center gap-3 mb-4">
                      {getRiskLevelIcon(riskLevel)}
                      <h3 className="text-lg font-semibold" style={{ color: colors.heading }}>
                        Risk Level
                      </h3>
                    </div>
                    <div className={`px-4 py-2 rounded-lg border text-center font-semibold ${riskLevel === 'High' ? 'text-red-600 bg-red-50 border-red-200' :
                      riskLevel === 'Moderate' ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
                        'text-green-600 bg-green-50 border-green-200'
                      }`}>
                      {riskLevel} Risk
                    </div>
                    <p className="text-sm mt-3" style={{ color: colors.text }}>
                      {riskLevel === 'High' && 'Consider consulting with a healthcare provider for further evaluation.'}
                      {riskLevel === 'Moderate' && 'Monitor your symptoms and consider lifestyle changes.'}
                      {riskLevel === 'Low' && 'Continue maintaining healthy sleep habits.'}
                    </p>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Detailed Results Section */}
            <motion.div
              className="mt-8 lg:mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="rounded-tr-3xl rounded-bl-3xl border border-black p-6 sm:p-8" style={{ backgroundColor: colors.background }}>
                <h2 className="text-xl sm:text-2xl font-bold mb-6" style={{ color: colors.heading }}>
                  Detailed Assessment Results
                </h2>

                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Risk Factors */}
                  {riskFactors.length > 0 && (
                    <div className="space-y-3">
                      <button
                        onClick={() => toggleSection('riskFactors')}
                        className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                        style={{ backgroundColor: colors.input, borderColor: colors.input }}
                      >
                        <div className="flex items-center gap-2">
                          <FaExclamationTriangle style={{ color: colors.primary }} />
                          <span className="font-semibold" style={{ color: colors.heading }}>Risk Factors</span>
                        </div>
                        <span className="text-sm px-2 py-1 rounded-full" style={{ backgroundColor: colors.primary, color: 'white' }}>
                          {riskFactors.length}
                        </span>
                      </button>
                      {expandedSections.riskFactors && (
                        <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                          <div className="space-y-2">
                            {riskFactors.map((factor: string, index: number) => (
                              <div key={index} className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                                <span className="text-sm" style={{ color: colors.text }}>{factor}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recommendations */}
                  {recommendations.length > 0 && (
                    <div className="space-y-3">
                      <button
                        onClick={() => toggleSection('recommendations')}
                        className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                        style={{ backgroundColor: colors.input, borderColor: colors.input }}
                      >
                        <div className="flex items-center gap-2">
                          <FaCheckCircle style={{ color: colors.primary }} />
                          <span className="font-semibold" style={{ color: colors.heading }}>Recommendations</span>
                        </div>
                        <span className="text-sm px-2 py-1 rounded-full" style={{ backgroundColor: colors.primaryButton, color: 'white' }}>
                          {recommendations.length}
                        </span>
                      </button>
                      {expandedSections.recommendations && (
                        <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                          <div className="space-y-2">
                            {recommendations.map((recommendation: string, index: number) => (
                              <div key={index} className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: colors.primaryButton }}></div>
                                <span className="text-sm" style={{ color: colors.text }}>{recommendation}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Key Metrics */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: colors.input }}>
                      <FaCheckCircle style={{ color: colors.primary }} />
                      <span className="font-semibold" style={{ color: colors.heading }}>Key Metrics</span>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span style={{ color: colors.text }}>Risk Score:</span>
                          <span className="font-semibold" style={{ color: colors.heading }}>{riskScore}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text }}>Assessment Date:</span>
                          <span className="font-semibold" style={{ color: colors.heading }}>{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text }}>Status:</span>
                          <span className="font-semibold" style={{ color: colors.heading }}>Saved to Account</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Call to Action */}
            <section className="text-center mb-8 sm:mb-12 mt-16 sm:mt-20">
              <div
                className="p-8 lg:p-12 rounded-tr-3xl rounded-bl-3xl border border-black"
                style={{ backgroundColor: colors.background }}
              >
                <h2
                  className="text-3xl sm:text-4xl font-bold mb-6"
                  style={{ color: colors.heading }}
                >
                  Track Your Sleep Health Progress
                </h2>
                <p
                  className="text-lg mb-8 max-w-3xl mx-auto"
                  style={{ color: colors.text }}
                >
                  Your assessment has been saved to your account. Regular assessments help you monitor changes in your sleep health and track the effectiveness of lifestyle modifications or treatments.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      trackGtmButtonClick({
                        button_id: 'results_auth_cta_history',
                        section: 'page_results_authenticated',
                        label: 'View Assessment History',
                        destination: '/assessment/history',
                      });
                      handleViewHistory();
                    }}
                    className="px-8 py-4 rounded-full font-semibold text-white text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                    style={{ backgroundColor: colors.primary }}
                  >
                    View Assessment History
                  </button>
                  <button
                    onClick={() => {
                      trackGtmButtonClick({
                        button_id: 'results_auth_cta_new_assessment',
                        section: 'page_results_authenticated',
                        label: 'Take New Assessment',
                        destination: '/assessment',
                      });
                      handleRetakeAssessment();
                    }}
                    className="px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                    style={{ backgroundColor: colors.secondaryButton, color: colors.text }}
                  >
                    Take New Assessment
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthenticatedResults;
